import React, { useMemo } from 'react'

import { CanvasRendererProvider } from '@sketch-hq/sketch-web-renderer'
import {
  AnnotationQueryVariablesProvider,
  RouteParams,
  getVersionRenderStatus,
} from '@sketch/modules-common'
import { RouteComponentProps } from 'react-router-dom'

import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'
import {
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
  useVersioning,
} from '../../../../versioning'
import { useGetVersionQueryWithPolling } from '../../../operations'
import PageAnnotationsOverlayContext from '../../../PageCanvasView/components/PageAnnotationsOverlayContext'
import { FrameInspectorProvider } from '../../../FrameView/components/FrameInspectorProvider'
import {
  CanvasInteractionsProvider,
  CanvasSelectionProvider,
} from '@sketch/canvas'
import { CanvasLeftPanelResizingProvider } from '../../../../canvas/components/CanvasLeftPanel'
import { GridsAndLayoutsProvider } from '../../../components/GridsAndLayoutsProvider'
import { useGetActiveSidebarTab } from '../../../components/ShareSidebarTabContext'
import { INSPECTOR_SEGMENT } from '../../../constants'
import { SectionView } from '../SectionView'

type SectionQueriesRouteProps = RouteComponentProps<
  Partial<RouteParams<'PAGE_SECTION'>>
>

export interface SectionQueriesProps
  extends SectionQueriesRouteProps,
    DocumentSidebarLayoutExtraProps {}

/**
 * Entry point for the single-Section focus view - the Section equivalent of
 * FrameQueries. The section's own identity (name, isVariantSet) is fetched
 * via GQL (getSectionsForPage, in SectionView/SectionSelector/
 * SectionsNavigationItem); this component only needs the page/version data
 * that the plain page canvas view also needs.
 */
export const SectionQueries = ({
  match,
  ...layoutProps
}: SectionQueriesProps) => {
  const { pageUUID, sectionUUID } = match.params

  const activeSidebarTab = useGetActiveSidebarTab()
  const isInspectPanelOpen = activeSidebarTab === INSPECTOR_SEGMENT

  const versioning = useVersioning()
  const { share, versionShortId, versionIdentifier, isViewingLatestVersion } =
    versioning

  const currentVersion = !versioning.loading ? versioning.currentVersion : null

  const { data, error } = useGetVersionQueryWithPolling({
    variables: {
      shareIdentifier: share.identifier,
      versionShortId,
    },
  })

  const subjects = useMemo(
    () => [{ type: 'PAGE' as const, permanentId: pageUUID! }],
    [pageUUID]
  )

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  const renderStatus = getVersionRenderStatus(data?.share?.version)
  const disableAnnotations = !share.commentsEnabled

  return (
    <CanvasRendererProvider>
      <CanvasInteractionsProvider>
        <CanvasSelectionProvider>
          <CanvasLeftPanelResizingProvider>
            <FrameInspectorProvider
              autoSelectRootInitially
              rootElementUUID={sectionUUID}
            >
              <GridsAndLayoutsProvider>
                <AnnotationQueryVariablesProvider
                  shareIdentifier={share.identifier}
                  versionIdentifier={versionIdentifier}
                  subjects={subjects}
                >
                  <PageAnnotationsOverlayContext
                    disabled={disableAnnotations}
                    hidden={isInspectPanelOpen}
                    isViewingLatestVersion={isViewingLatestVersion}
                    permanentPageId={pageUUID!}
                    sectionUUID={sectionUUID!}
                  >
                    <SectionView
                      layoutProps={layoutProps}
                      share={share}
                      currentVersion={currentVersion}
                      pageUUID={pageUUID!}
                      sectionUUID={sectionUUID!}
                      isViewingLatestVersion={isViewingLatestVersion}
                      renderStatus={renderStatus}
                      isInspectPanelOpen={isInspectPanelOpen}
                    />
                  </PageAnnotationsOverlayContext>
                </AnnotationQueryVariablesProvider>
              </GridsAndLayoutsProvider>
            </FrameInspectorProvider>
          </CanvasLeftPanelResizingProvider>
        </CanvasSelectionProvider>
      </CanvasInteractionsProvider>
    </CanvasRendererProvider>
  )
}
